import { useCallback, useLayoutEffect, useRef, useState } from 'react';

interface UseTabStripCapacityOptions {
  /** Number of document tabs competing for space in the strip. */
  itemCount: number;
  /**
   * Freeze capacity while true. The inline rename editor swaps a truncated tab
   * (~80px) for a ~200px input box; measuring then would fold that outlier into
   * the widest-tab tracking and could bump the tab being edited out of view
   * mid-rename. Measurement resumes — and re-runs — once this returns to false.
   */
  freezeMeasurement?: boolean;
  /**
   * Identity of the currently pinned/active tab. Changing it re-runs measurement
   * because the active tab renders a hair wider (border + shadow), which can push
   * a borderline strip just past its width and needs one fewer visible tab.
   */
  activeId?: string | null;
}

// Show every tab until the first real measurement, so the strip doesn't flash a
// collapsed set on mount.
const SHOW_ALL = Number.POSITIVE_INFINITY;

// gap-1 between tabs (0.25rem at the default root font size).
export const TAB_GAP_PX = 4;

/**
 * Marks the "+N more" trigger inside the measured row so width bookkeeping can
 * tell it apart from a real tab: its width is space that comes *back* when the
 * last overflowed tab is reclaimed, and it must never be mistaken for a tab
 * width when estimating whether another tab would fit.
 */
export const OVERFLOW_TRIGGER_ATTR = 'data-tab-overflow-trigger';

export interface CapacityDecisionInput {
  /** Space the strip has to work with (the scroll container's clientWidth). */
  available: number;
  /**
   * TRUE width of the row's contents, including the "+N more" trigger — summed
   * from the children rather than read off `scrollWidth`. See `measure` for why
   * `scrollWidth` cannot be used here.
   */
  content: number;
  /** Capacity currently rendered. Callers resolve SHOW_ALL to itemCount first. */
  current: number;
  itemCount: number;
  /**
   * Assumed width of the tab that would come back, used only when no measurement
   * is on record. The NARROWEST tab seen, deliberately: an optimistic guess that
   * grows one tab too eagerly is corrected on the very next pass by the overflow
   * check, whereas a pessimistic one silently leaves the strip under-filled.
   */
  growthTabWidth: number;
  /** Measured width of the "+N more" trigger, reclaimed when the last tab returns. */
  triggerWidth: number;
  /**
   * capacity → the content width measured the last time that capacity was
   * rendered. This is the hysteresis band, and it is *measured* rather than
   * estimated: growing back to capacity K is safe exactly when the strip is at
   * least as wide as K was last observed to need. Entries are overwritten with
   * each fresh observation rather than kept at a high-water mark, so a tab that
   * gets narrower (a shorter rename, an icon disappearing) releases the space it
   * used to reserve instead of holding it forever.
   */
  requiredWidths: ReadonlyMap<number, number>;
}

/**
 * Decide the next capacity for the strip. Pure, so the arithmetic can be tested
 * without a layout engine (jsdom has none).
 *
 * Shrinking is driven by direct observation — the row is wider than the space it
 * has, so give a tab back. Growing is the direction that shipped broken, for two
 * compounding reasons. It compared free space against the *widest tab ever seen*
 * while ignoring that reclaiming a tab also shrinks or retires the "+N more"
 * trigger; and, more fundamentally, the free space it compared was always zero
 * (see `measure`), so the condition could not fire at all. The strip collapsed
 * correctly and then never spread back out.
 *
 * Growth now takes the measured path first: each time a capacity is rendered we
 * record what it actually needed, and grow back into it once the strip is
 * genuinely that wide. That is exact, and it cannot oscillate — if growth into a
 * recorded capacity still overflows, the shrink re-records that capacity as
 * needing more than is available, which blocks the next attempt. When no record
 * exists (the documents changed) the estimate takes over, deliberately
 * optimistic: an over-eager grow is undone on the next pass, an over-cautious one
 * leaves the strip silently under-filled.
 */
export function nextTabStripCapacity({
  available,
  content,
  current,
  itemCount,
  growthTabWidth,
  triggerWidth,
  requiredWidths,
}: CapacityDecisionInput): number {
  // Overflowing: give a tab back. The 1px slack absorbs sub-pixel rounding.
  if (content > available + 1 && current > 1) {
    return current - 1;
  }
  if (current >= itemCount) {
    return current;
  }

  const target = current + 1;
  const measured = requiredWidths.get(target);
  if (measured !== undefined) {
    return available >= measured ? target : current;
  }

  // Nothing on record for `target` — the document set changed, so the recorded
  // widths described a row that no longer exists. Assume the returning tab is as
  // narrow as the narrowest seen, and credit the trigger's width when this growth
  // retires the overflow entirely, since that space goes straight back to tabs.
  const reclaimedTrigger = target === itemCount ? triggerWidth + TAB_GAP_PX : 0;
  return available - content + reclaimedTrigger >= growthTabWidth + TAB_GAP_PX
    ? target
    : current;
}

/**
 * Measure how many document tabs fit in the strip and expose that capacity, so
 * the rest overflow into a dropdown instead of a hidden horizontal scroll.
 *
 * The strip's available width (the scroll container's clientWidth) is compared
 * against the row's content width. Capacity steps one tab at a time and settles:
 * it drops while the row overflows, and grows back once the strip is at least as
 * wide as that capacity was last measured to need.
 *
 * The content width is SUMMED FROM THE CHILDREN, not read from `row.scrollWidth`.
 * The row is a block-level child filling its scroll container, so the CSSOM
 * clamps its `scrollWidth` to at least its own `clientWidth` — i.e. to the
 * container's width. Whenever the row is not overflowing, `scrollWidth` reports
 * exactly the available width and free space measures as zero no matter how empty
 * the strip really is. That silently disabled every width-based growth check in
 * the first cut of this hook; verified in Chromium (368px of tabs in a 600px
 * container reports `scrollWidth === 600`).
 *
 * Widths are bookkept per element kind: the "+N more" trigger is identified by
 * `OVERFLOW_TRIGGER_ATTR` and tracked separately, so it is never mistaken for a
 * tab. The narrowest-tab record drives the optimistic growth estimate, and both
 * records are safe from the ~200px rename editor because measurement is frozen
 * for the duration of a rename. State only changes when the capacity actually
 * changes, so a stable layout stops re-rendering.
 */
export function useTabStripCapacity({
  itemCount,
  freezeMeasurement = false,
  activeId,
}: UseTabStripCapacityOptions) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tabsRowRef = useRef<HTMLDivElement | null>(null);
  const minTabWidthRef = useRef(Number.POSITIVE_INFINITY);
  const triggerWidthRef = useRef(0);
  const requiredWidthsRef = useRef<Map<number, number>>(new Map());
  const lastItemCountRef = useRef(itemCount);
  // Mirrors `capacity` so the measurement pass can read it without doing its
  // width bookkeeping inside a state updater (updaters must stay pure).
  const capacityRef = useRef<number>(SHOW_ALL);
  const [capacity, setCapacity] = useState<number>(SHOW_ALL);

  const measure = useCallback(() => {
    // While a rename is in progress the strip holds a ~200px editor box; leave
    // capacity (and the width records) untouched until it resolves.
    if (freezeMeasurement) return;
    const container = scrollContainerRef.current;
    const row = tabsRowRef.current;
    if (!container || !row) return;

    const available = container.clientWidth;
    // Not laid out yet, or jsdom (which reports 0): leave capacity at SHOW_ALL.
    if (available === 0) return;

    // Adding or removing a document invalidates every recorded width — they
    // describe a row that no longer exists. Dropping them hands growth to the
    // estimate until each capacity is observed again.
    if (lastItemCountRef.current !== itemCount) {
      requiredWidthsRef.current.clear();
      minTabWidthRef.current = Number.POSITIVE_INFINITY;
      lastItemCountRef.current = itemCount;
    }

    // Sum the children rather than trusting row.scrollWidth — see the note above
    // on why the CSSOM makes scrollWidth useless for measuring free space.
    let content = 0;
    for (const child of Array.from(row.children)) {
      const el = child as HTMLElement;
      const width = el.offsetWidth;
      content += width + (content > 0 ? TAB_GAP_PX : 0);
      if (el.hasAttribute(OVERFLOW_TRIGGER_ATTR)) {
        triggerWidthRef.current = width;
      } else if (width > 0) {
        minTabWidthRef.current = Math.min(minTabWidthRef.current, width);
      }
    }

    const current =
      capacityRef.current === SHOW_ALL ? itemCount : capacityRef.current;

    // Record what this capacity needs right now. Overwriting (rather than keeping
    // a high-water mark) is what lets the strip reclaim space when tabs get
    // narrower; the value is an exact observation either way.
    requiredWidthsRef.current.set(current, content);

    const next = nextTabStripCapacity({
      available,
      content,
      current,
      itemCount,
      growthTabWidth: Number.isFinite(minTabWidthRef.current)
        ? minTabWidthRef.current
        : content,
      triggerWidth: triggerWidthRef.current,
      requiredWidths: requiredWidthsRef.current,
    });

    if (next === current) {
      // Everything fits and nothing wants to move; stay as-is (including at the
      // initial SHOW_ALL, so a strip that never overflows never re-renders).
      return;
    }
    capacityRef.current = next;
    setCapacity(next);
  }, [itemCount, freezeMeasurement]);

  // Re-measure after mount, after a tab is added/removed, after each capacity
  // step until it settles, when the active/pinned tab changes (it renders a hair
  // wider), and when a rename ends (measure's identity flips with the freeze).
  useLayoutEffect(() => {
    measure();
  }, [measure, capacity, activeId]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  return { scrollContainerRef, tabsRowRef, capacity };
}
